import {
  action,
  computed,
  IReactionDisposer,
  observable,
  reaction,
  runInAction,
} from "mobx";
import { Subscription } from "rxjs";
import { PurchaseEntity } from "@citychallenge/api-public";
import { CityStore } from "../../models/city-store";
import { ApiService } from "../api";
import { NotFoundError } from "../../errors";
import { PreferenceService } from "../preference";
import { GeoService } from "../geo";
import { createContext } from "react";

import { ServiceInterface } from "../service.interface";
import { TransTokens } from "../../i18n";
import { PurchaseService } from "../purchase";
import { AuthService } from "../auth";
import { Loadable, LoadableRequestOptions } from "../../loadable";
import { GameModel, CityModel, GameStore } from "../../models";
import { StorageService } from "../storage";
import { SessionService } from "../session";
import { Environment } from "@citychallenge/common";

const tokens = TransTokens.create({
  gameNotFoundError: "This game could not be found.",
  cityNotFoundError: "This city could not be found.",
});

export const CityServiceContext = createContext<CityService>(null as any);

export class CityService implements ServiceInterface {
  public context = CityServiceContext;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private geoService: GeoService,
    private preferenceService: PreferenceService,
    private purchaseService: PurchaseService,
    private sessionService: SessionService,
    private storageService: StorageService
  ) {}

  private subscriptions: (Subscription | IReactionDisposer)[] = [];

  // Little dirty fix to make sure purchases unlocked through redeem codes
  // are always applied to the game upon loading
  private localPurchases: PurchaseEntity[] = [];

  public serviceWillMount() {
    this.subscriptions.push(
      this.purchaseService.purchase$.subscribe(this.onProductDelivered)
    );

    this.subscriptions.push(
      reaction(
        () => this.authService.userId,
        (userId) => {
          userId ? this.addProductsOnLogin() : this.removeProductsOnLogout();
        }
      )
    );
  }

  public serviceWillUnmount() {
    for (const subscription of this.subscriptions) {
      typeof subscription === "function"
        ? subscription()
        : subscription.unsubscribe();
    }
  }

  public readonly cityStore = new CityStore(
    this.preferenceService.ACTIVE_CITY_ID,
    this.geoService.lastKnownLocation
  );

  @computed
  public get activeCity() {
    return this.cityStore.activeCity;
  }

  /**
   * This value is set to true when users' purchases have been loaded
   */
  @observable
  public userProductsLoaded = false;

  /**
   * Loop over all games in all city stores, calls callback function for each
   * game found
   * @param fn Callback function
   */
  private forEachGame = (fn: (game: GameModel) => void) => {
    for (const city of this.cityStore.cities) {
      for (const game of city.gameStore.games) {
        fn(game);
      }
    }
  };

  /**
   * A loadable that returns the citystore
   */
  public loadableCityStore = Loadable.create(async () => {
    const cities = await this.apiService.clientPublic.cities();

    const mappedCities = cities.data.map((city) => {
      return new CityModel(city);
    });

    this.cityStore.setCities(mappedCities);

    return this.cityStore;
  });

  private _loadableGameStoreMap: { [key: string]: Loadable<GameStore> } = {};

  @computed
  public get activeGameStore(): Loadable<GameStore> {
    return this.getLoadableGameStore(this.cityStore.activeCityId || "default");
  }

  private async getCombinedPurchases() {
    // Fetch all users remote purchases
    const purchases: PurchaseEntity[] = this.authService.isLoggedIn
      ? await this.purchaseService.getAllPurchases()
      : [];

    for (const purchase of this.localPurchases) {
      if (!purchases.find((p) => p.game.id === purchase.game.id)) {
        purchases.push(purchase);
      }
    }

    return purchases;
  }

  private getLoadableGameStore(cityId = "default") {
    if (this._loadableGameStoreMap[cityId])
      return this._loadableGameStoreMap[cityId];

    const loadable = Loadable.create(
      async () => {
        const cityStore = await this.loadableCityStore.ensuredValue();
        const city = cityStore.findCityByIdOrReturnClosest(cityId);

        if (!city)
          throw new NotFoundError(tokens.cityNotFoundError.interpolate());

        // Fetch all games from the server
        const rawGames = await this.apiService.clientPublic.gamesOfCity(
          city.id
        );

        // Map the games to GameModel with users' purchases included
        const games = rawGames.data.map((game) => {
          const gameEntity = new GameModel(
            game,
            this.storageService.getPreference(`unlocked_${game.id}`, false),
            city
          );

          return gameEntity;
        });

        for (const game of games) {
          // Not using await here to not block the loading thread
          const session = this.sessionService.modelForGame(game, []);
          if (session) game.activeSession = session;
        }

        // Hydrate games with app store product information
        await this.purchaseService.hydrateProducts(games);

        const purchases = await this.getCombinedPurchases();

        for (const game of games) {
          const gamePurchases = purchases.filter((p) => p.game.id === game.id);
          game.setPurchases(gamePurchases);
        }

        // Assign games to the game store
        city.gameStore.setGames(games);

        return city.gameStore;
      },
      {
        translatedContent: true,
        refreshInterval: 15 * 60,
      }
    );

    this._loadableGameStoreMap[cityId] = loadable;

    return loadable;
  }

  /**
   * Returns a loadable for the given cityId and gameId
   */
  public loadableGame(cityId: string, gameId: string, forceReload?: boolean) {
    const loadable = Loadable.create(
      async (options: LoadableRequestOptions) => {
        const gameStore = await this.getLoadableGameStore(cityId).ensuredValue(
          options
        );

        const game = gameStore?.findGameById(gameId);

        if (!game)
          throw new NotFoundError(tokens.gameNotFoundError.interpolate());

        return game;
      },
      {
        translatedContent: true,
        refreshInterval: 15 * 60,
        defaultRequestOptions: forceReload ? { reset: true } : undefined,
      }
    );

    return loadable;
  }

  /**
   * Purchase a game
   */
  public buy(game: GameModel) {
    return this.purchaseService.buy(game);
  }

  /**
   * Callback that adds newly delivered purchases to games
   */
  @action
  private onProductDelivered = (purchase: PurchaseEntity) => {
    this.forEachGame((game) => {
      if (game.id === purchase.game.id) game.setPurchases([purchase]);
    });

    this.localPurchases.push(purchase);
  };

  /**
   * Add all users' purchases to games on login. This makes sure that bought
   * games appear as 'owned' in the interface
   */
  private addProductsOnLogin = async () => {
    // Don't load purchases on web
    if (Environment.OS === "web") return;
    const purchases = await this.purchaseService.getAllPurchases();

    runInAction(() => {
      this.localPurchases = [];
      purchases.forEach(this.onProductDelivered);
      this.userProductsLoaded = true;
    });
  };

  /**
   * Remove users' products from all games on logout
   */
  @action
  private removeProductsOnLogout = () => {
    this.localPurchases = [];
    this.forEachGame((game) => (game.purchases = []));
    this.userProductsLoaded = false;
  };
}
